import * as PromiseUtils from 'blend-promise-utils-es5';

export { RetryOpts } from 'blend-promise-utils-es5';

/* eslint-disable prefer-destructuring */

/**
 * 等待一小段时间, 在等待结束后可以选择返回一个值
 * @description 此函数来自第三方库 [blend-promise-utils](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/delay.ts))
 * @example
 * ```js
 * await delay(100);
 * await delay(300, "foo");   // -> "foo"
 * ```
 */
export const delay = PromiseUtils.delay;

/**
 * 类似 [lodash.map](https://lodash.com/docs/4.17.15#map), 将一个数组的每一项输入异步转换函数 iteratee, 得到一个新的数组.
 * 本函数不保证异步任务按顺序完成, 但可以保证所有任务完成后, 其结果的顺序与原数组的顺序相同.
 *
 * 本函数同时允许输入一个对象, 但仍然返回一个数组, 而且无法保证其结果的顺序.
 *
 * 转换函数中的异步任务以最大 10 个并发并行执行.
 * @description 此函数来自第三方库 [blend-promise-utils](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/map.ts))
 * @example
 * ```js
 * // 1 ------------> | resolve
 * // 2 ---------> |
 * // 3 ------> |
 * // 4 ---> |
 * await mapPromise([1, 2, 3, 4], async x => delay(4 - x, x + 1));   // -> [2, 3, 4, 5]
 * ```
 */
export const mapPromise = PromiseUtils.map;

/**
 * 同 [[mapPromise]], 但是允许指定最大并行数量
 * @see [[mapPromise]]
 * @description 此函数来自第三方库 [blend-promise-utils](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/map.ts))
 * @example
 * ```js
 * // 1 ------------> |
 * // 2 ---------> |
 * //              3 ------> |
 * //                 4 ---> | resolve
 * await mapLimit([1, 2, 3, 4], 2, async x => delay(4 - x, x + 1));  // -> [2, 3, 4, 5]
 * ```
 */
export const mapLimit = PromiseUtils.mapLimit;

/**
 * 同 [[mapLimit]], 但一次只允许一个异步任务执行, 即串行执行了所有异步任务
 * @see [[mapLimit]]
 * @description 此函数来自第三方库 [blend-promise-utils](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/map.ts))
 * @example
 * ```js
 * // 1 ------------> |
 * //                 2 ---------> |
 * //                              3 ------> | resolve
 * await mapSeries([1, 2, 3], async x => delay(4 - x, x + 1));       // -> [2, 3, 4]
 * ```
 */
export const mapSeries = PromiseUtils.mapSeries;

/**
 * 给异步任务增加出错后进行重试, 直到异步任务成功完成的能力
 *
 * 可以通过以下可选参数自定义重试行为:
 * - maxAttempts: 最大重试次数, 默认为 0, 即**默认不会有任何重试行为**
 * - delayMs: 在重试之前等待的毫秒数, 默认为 0
 * - isRetryable: 一个用于判断是否要继续重试的函数. 函数被调用时会在参数上携带错误信息, 返回 truthy 值时表示需要进行重试. 注: isRetryable 函数必须是同步的
 * @returns 返回增加了重试的功能之后的函数
 * @description 此函数来自第三方库 [blend-promise-utils](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/retry.ts))
 * @example
 * ```js
 * retry(async () => http.get("foo"));                      // -> function
 * retry(async () => http.get("foo"), { delayMs: 1000 });   // -> function
 * retry(async () => http.get("foo"), { maxAttempts: 3 });  // -> function
 * retry(async () => http.get("foo"), { isRetryable: err => err.status !== 401 });  // -> function
 * await retry(async () => http.get("foo"))();  // -> HTTP Response
 * ```
 */
export const retry = PromiseUtils.retry;

/* eslint-enable prefer-destructuring */
